import React, { useEffect, useRef, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import api from '../lib/api';
import Editor from '../components/Wiki/Editor';
import LinkedTasks from '../components/Wiki/LinkedTasks';
import LinkedWorklogs from '../components/Wiki/LinkedWorklogs';
import Markdown from '../components/Markdown';
import Provenance from '../components/Wiki/Provenance';
import TagEditor from '../components/Wiki/TagEditor';
import Toc from '../components/Wiki/Toc';
import TopBar, { TBTN, TBTN_DANGER, TBTN_PRIMARY } from '../components/Wiki/TopBar';
import WikiLayout, { DOC } from '../layouts/WikiLayout';
import useWikiEditor from '../hooks/useWikiEditor';
import { UNTITLED } from '../components/Wiki/shell';
import type { WikiPageArticle } from '../types/wiki';

type WikiPageProps = {
  page: WikiPageArticle;
  // 本人用过的标签，给标签输入框的 datalist 当候选。
  allTags: string[];
};

type WikiPageComponent = React.FC<WikiPageProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

// 阅读态与编辑态是同一份内容的两个形态，共用顶条、标题位置与出处行 ——
// 所以编辑态是这一页里的一个开关，不是 /wiki/:url_key/edit 那样的独立路由。
// 独立路由等于把出处行复制第二份，而它当初存在的理由就是「author_kind 要有
// 读者」，复制它就是让两处开始漂。
const WikiPageView: WikiPageComponent = ({ page, allTags }) => {
  const [deleting, setDeleting] = useState(false);
  // 新建过来时直接进编辑态：建页的下一个动作一定是写正文（或先给它起个名）。
  // 用查询串而不是 Inertia 的 state —— 后者在整页重载后就没了，而这一跳恰好
  // 是 router.visit（整页导航）。
  //
  // 两个信号，差别只在**光标落在哪**：
  //   ?new=1  —— 这一页还没被命名（标题是 shell.ts 里那个 UNTITLED 占位词），
  //              光标落在标题上并全选，第一个按键就把占位词替换掉
  //   ?edit=1 —— 标题已知（从「建「<搜索词>」这一页」那个出口进来的），
  //              光标落在正文
  const [entry] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === '1') return 'new';
    if (params.get('edit') === '1') return 'edit';
    return null;
  });
  const [editing, setEditing] = useState(entry !== null);
  const namingNew = entry === 'new';

  // 读完就把这两个参数从地址里摘掉。
  //
  // 它们是那一跳的**一次性信号**，留在地址栏里就变成了这一页的持久属性：
  // 收藏这个地址、刷新、或者过一会儿后退回来，都会重新进编辑态 —— 而那时人
  // 想做的通常只是读一遍。
  //
  // 摘在**进来这一刻**而不是点「完成」那一刻：后者的话，整个编辑期间刷新仍然
  // 会回到编辑态，那个坑只是变窄了没有被堵住。
  //
  // 传 window.history.state 进去（而不是 null）：Inertia 把它那份 page 对象存在
  // history state 里，覆盖成 null 会让前进/后退失去恢复的依据。它记着的 url 里
  // 可能仍有这两个参数，所以「后退再前进」这一条路上编辑态还是可能回来 ——
  // 那与改之前一样，不是这次新造的。
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('edit') && !url.searchParams.has('new')) return;

    url.searchParams.delete('edit');
    url.searchParams.delete('new');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  // 标题的状态在这里而不在 Editor 里：阅读态的 <h1> 与编辑态的输入框必须占
  // 同一个位置（都在出处行上面），否则一进编辑态标题就掉到出处行下面，页面的
  // 层级当场倒过来。留在父组件让那个位置只有一处定义。
  const [title, setTitle] = useState(page.title);
  // 下面那个 effect 要拿它送光标。用 ref 而不是 autoFocus：autoFocus 只能「聚焦」，
  // 没法顺带全选，而全选是这个手感的一半。
  const titleRef = useRef<HTMLInputElement>(null);

  // 新建落地时把光标送到标题上，并**全选**那个占位词 —— 第一个按键直接替换掉，
  // 手感等于一个空标题框（Finder 新建文件夹改名那个模式）。
  //
  // 只在挂载时做一次（entry 之后不会变）。不写成标题框上的 onFocus 全选：
  // 那样每次点标题都会整条选中，改一个错字都得先取消选择。
  useEffect(() => {
    if (!namingNew) return;

    const input = titleRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [namingNew]);

  // 编辑态那台状态机。放在页面这一层是因为它有两个渲染者：顶条要 status /
  // saving / save，正文区要 body / 冲突横幅（见 hooks/useWikiEditor.ts）。
  //
  // 无条件调用（不能塞进 editing 分支里）—— hook 的调用次序在每次渲染之间必须
  // 一致。不在编辑态时它只是持着一份没人读的草稿，代价是零。
  const editor = useWikiEditor({ page, title, onDone: () => setEditing(false) });

  // 二次确认用浏览器原生 confirm。不引 antd 的 Modal（/todo 刻意把 antd 挡在
  // 外面），也不做 TaskContextMenu 那种内联二次确认 —— 那个组件是为页面树准备
  // 的，这一期没有树，为一个按钮把它挪过来就是造一个没有调用方的组件（§17.5.5）。
  const destroy = async () => {
    if (!window.confirm(`删掉「${page.title}」？可以在回收站里恢复。`)) return;

    setDeleting(true);
    try {
      await api.wikiPages.destroy(page.url_key);
      // 删完这一页就不存在了，留在这里会是个 404，所以回列表。
      router.visit('/wiki');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TopBar
        trail={[{ label: page.title }]}
        actions={
          editing ? (
            <>
              {/* 状态与错误紧贴「保存」：它们讲的都是这一次写入的结果。 */}
              {editor.error ? (
                <span className="mr-1.5 text-[12.5px] text-todo-urgent">{editor.error}</span>
              ) : (
                editor.status && (
                  <span className="mr-1.5 text-[12.5px] text-todo-muted">{editor.status}</span>
                )
              )}
              <button
                type="button"
                onClick={editor.save}
                disabled={editor.saving}
                className={TBTN_PRIMARY}
              >
                {editor.saving ? '保存中…' : '保存'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className={TBTN}>
                完成
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setEditing(true)} className={TBTN}>
                编辑
              </button>
              <Link href={`/wiki/${page.url_key}/versions`} className={TBTN}>
                历史
              </Link>
              <button
                type="button"
                onClick={destroy}
                disabled={deleting}
                className={TBTN_DANGER}
              >
                {deleting ? '删除中…' : '删除'}
              </button>
            </>
          )
        }
      />

      <div className={DOC}>
        {/* 标题、出处行、分隔线在两种形态下都在同一个位置 —— 只有标题从 <h1>
            换成输入框、正文从渲染结果换成 textarea。

            设计稿这里还有一个 66px 的页面 emoji。这一期没有 icon 列，所以那一格
            **不渲染**，而不是渲染一个占位的 📄：给每一页戴同一顶帽子比不戴更吵，
            而且会让人以为它可以改。 */}
        {editing ? (
          <input
            ref={titleRef}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={UNTITLED}
            className="w-full border-0 bg-transparent p-0 text-[40px] font-bold leading-[1.15]
                       tracking-tight text-todo-ink outline-none placeholder:text-todo-line2"
          />
        ) : (
          <h1 className="text-[40px] font-bold leading-[1.15] tracking-tight">{page.title}</h1>
        )}

        <div className="mt-3.5 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <Provenance page={page} />
          <span className="text-todo-line2">·</span>
          {/* 编辑正文时标签退回纯文本：TagEditor 存完会 router.reload 把 props
              重新取一遍，而那会把编辑器里还没自动存的最后两秒输入冲掉。
              改标签本来也不该在写正文的中途发生。

              设计稿在编辑态画了一个可编辑的标签输入框 —— 不照它做，理由就是
              上面这条。 */}
          {editing ? (
            page.tags.length > 0 && (
              <span className="text-[13px] text-todo-muted">{page.tags.join(' · ')}</span>
            )
          ) : (
            <TagEditor page={page} allTags={allTags} />
          )}
        </div>

        <hr className="mb-2 mt-6 border-todo-line" />

        {/* 关联的任务/日志只在阅读态出现，与下面的 Toc 同一个理由：编辑态下
            这块信息不是这次操作要看的东西，挤在 textarea 上方只会碍事。 */}
        {!editing && (
          <>
            <LinkedTasks tasks={page.linked_tasks} />
            <LinkedWorklogs worklogs={page.linked_worklogs} />
          </>
        )}

        {editing ? (
          // autoFocus 传 false 是「正在给新页起名」那一刻：光标该在标题上，
          // 正文框不能抢走它。
          <Editor editor={editor} onDone={() => setEditing(false)} autoFocus={!namingNew} />
        ) : page.body_md ? (
          <Markdown size="article" className="text-todo-ink">
            {page.body_md}
          </Markdown>
        ) : (
          <p className="text-[13.5px] text-todo-muted">这一页还没有正文。</p>
        )}
      </div>

      {/* 目录只在阅读态出现：编辑态下正文是一个 textarea，里面的标题没有 DOM
          节点可以跳过去 —— 一份点了没反应的目录比没有目录更糟。 */}
      {!editing && <Toc bodyMd={page.body_md} />}
    </>
  );
};

WikiPageView.layout = (page: React.ReactNode) => <WikiLayout>{page}</WikiLayout>;

export default WikiPageView;
